import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { KeyboardArrowRight } from "@mui/icons-material";
import Mixpanel from "utils/mixpanel";
import { COLORS } from "components/v3/Theme/colors";
import { PATHS } from "constants/pathConstants";
import SuggestedScreeningCard from "components/v3/sharedComponents/SuggestedScreeningCard";
import PageTitle from "utils/PageTitle";
import TestKitSelectionFooterBar from "./TestKitSelectionFooterBar";
import Translate from "translate/Translate";
import {
  selectTestKitsGLIsCurrentlyOffering,
  selectExistingOrdersWithoutCancelled
} from "store/reducers/order/OrderInformationSlice";
import { selectAllPreviousSurveyAnswers } from "store/reducers/survey/SurveySlice";
import { setKitsSelectedToBeOrdered } from "store/reducers/order/OrderInformationSlice";
import { hasAnsweredAllCriticalKitRequestQuestions } from "utils/v3/surveyUtils";
import { pushDescriptionIntoKitObject } from "utils/v3/testKitUtils";

import { downloadFile } from "utils/utils";
import { useDispatch, useSelector } from "react-redux";

import { downloadOrderDocuments } from "apis/ordersApi";
import { figureOutKitsToOfferMemberRightNow } from "utils/v3/testKitUtils";
import { Link, useLocation, useHistory } from "react-router-dom";
import { selectUserPersonalInformation } from "store/reducers/user/UserAccountSlice";
import SuggestedScreeningsConfirmationModal from "components/v3/sharedComponents/modals/SuggestedScreeningsConfirmationModal";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";

const TestKitSelectionPage = (props) => {
  const availableKits = useSelector(selectTestKitsGLIsCurrentlyOffering);
  const kitsGLCurrentOffers = pushDescriptionIntoKitObject(availableKits);
  const existingTestKitOrders = useSelector(
    selectExistingOrdersWithoutCancelled
  );
  const existingTestKitOrdersWithDocs = existingTestKitOrders.filter(
    (order) => {
      return order.documentIds?.length > 0;
    }
  );
  const history = useHistory();
  const location = useLocation();
  const dispatchReducer = useDispatch();
  const previousSurveyAnswers = useSelector(selectAllPreviousSurveyAnswers);
  const userInformation = useSelector(selectUserPersonalInformation);
  const kitSuggestionOverrideToken = location.search;

  const kitsToOfferMemberRightNow = figureOutKitsToOfferMemberRightNow(
    kitsGLCurrentOffers,
    existingTestKitOrders,
    previousSurveyAnswers,
    kitSuggestionOverrideToken,
    userInformation.glProgramId
  );
  const hasKitsToOfferMemberRightNow = kitsToOfferMemberRightNow.length > 0;
  const suggestedOffersUuids = kitsToOfferMemberRightNow?.map(
    (suggestedKit) => {
      return suggestedKit.test_uuid;
    }
  );

  const [
    uuidsOfKitsSelectedToBeOrdered,
    setUuidsOfKitsSelectedToBeOrdered
  ] = useState(
    kitsToOfferMemberRightNow.map((kit) => {
      return kit.test_uuid;
    })
  );
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const getKitFiles = (testUuid) => {
    if (testUuid) {
      const exactId = existingTestKitOrdersWithDocs.filter(
        (item) => item.glTestUUID === testUuid
      );
      return exactId && exactId[0] && exactId[0].documentIds
        ? exactId[0].documentIds
        : [];
    }

    return [];
  };

  const downloadDocument = (orderId, documentId) => {
    downloadOrderDocuments(orderId, documentId)
      .then((response) => {
        downloadFile(documentId, response.data);
        Mixpanel.track(`Test result downloaded`, {
          documentId: documentId
        }).catch((error) => {
          Mixpanel.track(`Test result document not found`, {
            error: error,
            documentId: documentId
          });
        });
      })
      .catch((error) => {
        Mixpanel.track(`Test result download failed`, {
          error: error,
          documentId: documentId
        });
      });
  };

  function downloadFiles(uuid) {
    const currentOrder = existingTestKitOrdersWithDocs.filter(
      (orders) => orders.glTestUUID === uuid
    );
    const orderId = currentOrder[0].glOrderId;
    const filesPdfIds = getKitFiles(uuid);

    if (filesPdfIds && filesPdfIds.length > 0) {
      filesPdfIds.map((fileId) => {
        return downloadDocument(orderId, fileId);
      });
    }
  }

  const addTest = (uuid) => {
    if (uuidsOfKitsSelectedToBeOrdered.indexOf(uuid) === -1) {
      Mixpanel.track("Test added", {
        testUuid: uuid
      });

      setUuidsOfKitsSelectedToBeOrdered([
        ...uuidsOfKitsSelectedToBeOrdered,
        uuid
      ]);
    }
  };

  const removeTest = (uuid) => {
    Mixpanel.track("Test removed", {
      testUuid: uuid
    });

    const filteredKitUuidList = Object.assign(
      [],
      uuidsOfKitsSelectedToBeOrdered.filter((id) => {
        return id !== uuid;
      })
    );
    setUuidsOfKitsSelectedToBeOrdered(filteredKitUuidList);
  };

  function isSelected(uuid) {
    return uuidsOfKitsSelectedToBeOrdered.includes(uuid);
  }

  const selectOrDeselectKit = (uuid) => {
    if (isSelected(uuid)) {
      removeTest(uuid);
    } else {
      addTest(uuid);
    }
  };
  const submitOrder = (uuids) => {
    Mixpanel.track("Kits selected, proceeding to order forms");
    dispatchReducer(setKitsSelectedToBeOrdered(uuids));
    history.push({
      pathname: PATHS.ORDER_KIT_INFORMED_CONSENT_SURVEY,
      search: location.search
    });
  };

  const renderBundleCards = () => {
    return (
      <Box
        sx={{
          gap: {
            xs: 3,
            lg: "30px"
          },
          margin: "auto",
          userSelect: "none",
          ...(kitsGLCurrentOffers?.length > 1 && {
            display: {
              xs: "flex",
              lg: "grid"
            },
            flexDirection: {
              xs: "column"
            },
            gridAutoRows: {
              lg: "1fr"
            },
            gridTemplateColumns: {
              lg: "1fr 1fr 1fr"
            }
          }),
          ...(kitsGLCurrentOffers?.length === 1 && {
            display: "flex"
          })
        }}
      >
        {kitsGLCurrentOffers?.map((kit, index) => {
          if (
            userInformation.glProgramId === "Genetic Screen" &&
            kit.glTestType !== "GHS"
          ) {
            return null;
          }

          const kitIsSuggested = !!kitsToOfferMemberRightNow.find(
            (suggestedKit) => suggestedKit.test_uuid === kit.test_uuid
          );

          return (
            <Box
              key={`suggested-screening-card-${index}-${kit.test_uuid.slice(
                0,
                2
              )}`}
              sx={{ userSelect: "none" }}
            >
              <SuggestedScreeningCard
                displayOnly={false}
                downloadFiles={downloadFiles}
                existingTestKitOrders={existingTestKitOrders}
                hasFilesReadyToDownload={getKitFiles(kit.test_uuid).length > 0}
                kit={kit}
                onSelect={selectOrDeselectKit}
                selected={isSelected(kit.test_uuid)}
                suggested={kitIsSuggested}
                userHasOrder={existingTestKitOrders.length > 0}
              />
            </Box>
          );
        })}
      </Box>
    );
  };

  PageTitle("Request a screening");

  useEffect(() => {
    // If there's an override token and required questions aren't answered, redirect to kit request survey
    if (
      kitSuggestionOverrideToken &&
      !hasAnsweredAllCriticalKitRequestQuestions(previousSurveyAnswers)
    ) {
      history.push({
        pathname: PATHS.KIT_REQUEST_SURVEY,
        search: kitSuggestionOverrideToken
      });
      return;
    }

    // If these aren't satisfied, we can't offer kits before a consult
    if (!hasKitsToOfferMemberRightNow) {
      history.push(PATHS.LANDING_PAGE);
    }
  }, []);

  return (
    <Box
      sx={{
        background:
          "linear-gradient(180deg, #F2F7FB 0%, rgba(242, 247, 251, 0.00) 62.6%)",
        minHeight: "100dvh",
        paddingBottom: {
          xs: "76px",
          md: "92px"
        }
      }}
    >
      <GradientFramedLayout showHeader={true} showFooter={false}>
        <Box
          sx={{
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: "30px",
            margin: "auto",
            maxWidth: "1200px",
            padding: {
              xs: "32px 20px",
              xl: "60px 0px"
            },
            width: "100%"
          }}
        >
          <Box
            sx={{
              alignItems: "flex-start",
              display: "flex",
              flexDirection: "column",
              gap: "30px",
              maxWidth: "660px"
            }}
          >
            <Box
              sx={{
                alignItems: "flex-start",
                display: "flex",
                flexDirection: "column",
                gap: "10px"
              }}
            >
              <Typography
                sx={{ display: { xs: "none", lg: "block" } }}
                variant="h4_primary"
              >
                <Translate text="Your Suggested DNA Screenings" />
              </Typography>
              <Typography
                sx={{ display: { xs: "block", lg: "none" } }}
                variant="h4_primary"
              >
                <Translate text="Your Test Kits" />
              </Typography>
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "2px"
                }}
              >
                <Link
                  to="/"
                  name="Dashboard"
                  style={{ textDecoration: "none" }}
                >
                  <Typography
                    sx={{ "&:hover": { textDecoration: "underline" } }}
                    variant="body_dark_gray"
                  >
                    <Translate text="Dashboard" />
                  </Typography>
                </Link>
                <KeyboardArrowRight
                  sx={{
                    color: COLORS.DARK_GRAY,
                    height: "20px",
                    width: "20px"
                  }}
                />
                <Typography
                  sx={{ display: { xs: "none", lg: "block" } }}
                  variant="body_dark_gray"
                >
                  <Translate text="Your Suggested DNA Screenings" />
                </Typography>
                <Typography
                  sx={{ display: { xs: "block", lg: "none" } }}
                  variant="body_dark_gray"
                >
                  <Translate text="Request A Kit" />
                </Typography>
              </Box>
            </Box>
            <Typography variant="body">
              <Translate text="Based on the health information you shared, these DNA screening tests can help support your health goals." />
            </Typography>
          </Box>
          {renderBundleCards()}
        </Box>
      </GradientFramedLayout>
      <TestKitSelectionFooterBar
        onContinue={() => {
          Mixpanel.track("Test selection confirmation modal opened");

          setConfirmationModalOpen(true);
        }}
        onSubmit={(uuids) => {
          submitOrder(uuids);
        }}
        kitsToOfferMemberRightNow={kitsToOfferMemberRightNow}
        selectedKitUuids={uuidsOfKitsSelectedToBeOrdered}
        suggestedOffersUuids={suggestedOffersUuids}
      />
      <SuggestedScreeningsConfirmationModal
        existingTestKitOrders={existingTestKitOrders}
        isOpen={confirmationModalOpen}
        kitOffers={kitsGLCurrentOffers}
        kitsToOfferMemberRightNow={kitsToOfferMemberRightNow}
        onCloseModal={() => {
          setConfirmationModalOpen(false);
        }}
        onSubmit={({ add = false }) => {
          const uuids = add
            ? suggestedOffersUuids
            : uuidsOfKitsSelectedToBeOrdered;

          Mixpanel.track(
            add
              ? "Selected option to add all unselected suggested tests from the test selection confirmation modal"
              : "Selected option to continue with their existing selected tests from the test selection confirmation modal"
          );
          submitOrder(uuids);
        }}
        selectedKitUuids={uuidsOfKitsSelectedToBeOrdered}
      />
    </Box>
  );
};

export default TestKitSelectionPage;
